/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef} from 'react';
import {FLYOUT_SUBMENU_ITEM_THRESHOLD} from '@ion/dls/src/components/navigation/constants';
import {MenuItem, MenuItems} from '@ion/dls/src/types/components';

type NullableHeight = number | null;

/**
 * Defines the app-specific callbacks and state required by
 *
 * The hook intentionally accepts callback props instead of importing
 * application-shell actions or route APIs directly. This keeps the current-theme
 * navigation rules reusable from `@ion/applications` while allowing each host
 * application to decide how Redux state and router navigation are performed.
 */
interface CurrentThemeNavigationParams {
  activeIndex: number;
  currentPage: string;
  isCurrent: boolean;
  isDesktop: boolean;
  menuItems: MenuItems;
  secondaryNavigationOpen?: boolean;
  markSkipNextRouteSync: () => void;
  navigateToPage: (page: string) => void;
  setActiveIndex: (index: number) => void;
  setLayoutOffsetHeight: (height: NullableHeight) => void;
  toggleSecondaryNavigation: (show: boolean) => void;
}

/**
 * Defines a secondary-page click from the DLS navigation shell.
 */
interface SecondaryPageParams {
  index: number;
  isSecondaryIndex?: boolean;
  page: string;
}

/**
 * Defines a primary navigation item click that may have child pages.
 */
interface ParentPageParams {
  index: number;
  isSecondaryIndex?: boolean;
  item?: MenuItem;
}

const getNavigationItemKey = (item: MenuItem, index: number): string => (
  item?.id || item?.key || item?.page || `${index}`
);

/**
 * Centralizes iOn app behavior required by the current-theme DLS navigation.
 *
 * The shared DLS navigation shell owns rendering and local interaction state,
 * while the iOn shell owns route changes, Redux state, and layout offset. This
 * hook bridges those two layers for current-theme-only behavior:
 *
 * - remembers the last selected submenu page per primary navigation item;
 * - opens large child menus in the fixed top navigation when they meet
 *   {@link FLYOUT_SUBMENU_ITEM_THRESHOLD};
 * - keeps secondary navigation open when selecting current-theme secondary
 *   pages on desktop;
 * - opens smaller child menus inline under their primary item while preserving
 *   the submenu that contains the active route;
 * - applies and clears the page layout offset used by the fixed top navigation.
 *
 * @param params Current navigation state plus host callbacks for route and
 * Redux updates.
 * @returns Current-theme navigation handlers consumed by the host `SideNav`.
 */
export const useCurrentThemeNavigation = ({
  activeIndex,
  currentPage,
  isCurrent,
  isDesktop,
  markSkipNextRouteSync,
  menuItems,
  navigateToPage,
  secondaryNavigationOpen,
  setActiveIndex,
  setLayoutOffsetHeight,
  toggleSecondaryNavigation
}: CurrentThemeNavigationParams) => {
  const lastSubmenuPageRef = useRef<Record<string, string>>({});

  /**
   * Resolves the submenu page to open when the user clicks a primary item that
   * uses top navigation. The priority is: last user-selected child, current
   * page child, then first child.
   */
  const getLastNavigatedSubmenu = useCallback((item: MenuItem, index: number): MenuItem => {
    const parentKey = getNavigationItemKey(item, index);
    const lastPage  = lastSubmenuPageRef.current[parentKey];
    return item?.pages?.find(({page}) => page === lastPage) ||
      item?.pages?.find(({page}) => page === currentPage) ||
      item?.pages?.[0] as MenuItem;
  }, [currentPage]);

  /**
   * Applies top-navigation layout offset for current-theme pages.
   */
  const handleTopNavigationVisibilityChange = useCallback((height: NullableHeight) => {
    setLayoutOffsetHeight(isCurrent ? height : null);
  }, [isCurrent, setLayoutOffsetHeight]);

  useEffect(() => () => {
    setLayoutOffsetHeight(null);
  }, [setLayoutOffsetHeight]);

  /**
   * Handles current-theme secondary-page selection.
   *
   * On desktop, the route is changed while secondary navigation stays open. The
   * selected page is also remembered for the active parent so future primary
   * clicks return the user to their last submenu.
   *
   * @returns `true` when the hook fully handled the interaction; otherwise
   * `false` so the caller can fall back to legacy/shared navigation behavior.
   */
  const handleCurrentSecondaryPage = useCallback(({
    index,
    isSecondaryIndex,
    page
  }: SecondaryPageParams): boolean => {
    if (!isCurrent || !isSecondaryIndex) {
      return false;
    }
    markSkipNextRouteSync();
    const parentIndex = Number.isInteger(index) ? index : activeIndex;
    const parent      = menuItems[parentIndex];
    if (parent) {
      lastSubmenuPageRef.current[getNavigationItemKey(parent, parentIndex)] = page;
    }
    if (!isDesktop) {
      return false;
    }
    setActiveIndex(parentIndex);
    toggleSecondaryNavigation(true);
    if (page && page !== currentPage) {
      navigateToPage(page);
    }
    return true;
  }, [
    activeIndex,
    currentPage,
    isCurrent,
    isDesktop,
    markSkipNextRouteSync,
    menuItems,
    navigateToPage,
    setActiveIndex,
    toggleSecondaryNavigation
  ]);

  /**
   * Handles current-theme primary items that own secondary pages.
   *
   * Large child menus activate the fixed top navigation and navigate to the
   * last selected child. Smaller child menus are opened inline under the
   * clicked primary item without changing routes.
   *
   * @returns `true` when the hook fully handled the interaction; otherwise
   * `false` so the caller can continue with the standard route switch logic.
   */
  const handleCurrentParentWithSecondaryNavigation = useCallback(({
    index,
    isSecondaryIndex,
    item
  }: ParentPageParams): boolean => {
    const hasSecondaryItems = !!item?.pages?.length;
    if (!isCurrent || isSecondaryIndex || !hasSecondaryItems) {
      return false;
    }
    const shouldUseTopNavigation = item.pages.length >= FLYOUT_SUBMENU_ITEM_THRESHOLD;
    if (shouldUseTopNavigation) {
      const submenuItem = getLastNavigatedSubmenu(item, index);
      setActiveIndex(index);
      toggleSecondaryNavigation(true);
      if (submenuItem?.page && submenuItem.page !== currentPage) {
        navigateToPage(submenuItem.page);
      }
      return true;
    }
    const isSameParent = activeIndex === index && secondaryNavigationOpen;
    if (isSameParent) {
      markSkipNextRouteSync();
      toggleSecondaryNavigation(false);
      return true;
    }
    setActiveIndex(index);
    toggleSecondaryNavigation(true);
    return true;
  }, [
    activeIndex,
    currentPage,
    getLastNavigatedSubmenu,
    isCurrent,
    isDesktop,
    markSkipNextRouteSync,
    navigateToPage,
    secondaryNavigationOpen,
    setActiveIndex,
    toggleSecondaryNavigation
  ]);

  return {
    handleCurrentParentWithSecondaryNavigation,
    handleCurrentSecondaryPage,
    handleTopNavigationVisibilityChange
  };
};

export default useCurrentThemeNavigation;
